import React, { useContext, useState } from 'react'
import { Menu, MenuItem } from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'
import { Permissions } from '@marketing-milk/interfaces'
import { ProfilePicture, sessionContext, useGetBusinessIDIfExists } from '@marketing-milk/frontend'

interface Props {
  expanded: boolean
  /** Lets the rail hold a hover-peek open while this menu is up. */
  onOpenChange?: (open: boolean) => void
}

export const RailUserMenu = ({ expanded, onOpenChange }: Props) => {
  const businessID = useGetBusinessIDIfExists()
  const { user, logout } = useContext(sessionContext)
  const { isAuthenticated, logout: auth0Logout } = useAuth0()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  function setOpen(el: HTMLElement | null) {
    setAnchor(el)
    onOpenChange?.(Boolean(el))
  }

  const initials = user ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}` : 'n/a'

  function logoutUser() {
    const redirectURL = 'https://www.marketingmilk.com/'
    logout()
    // An Auth0 login leaves a second session behind, so clearing ours isn't enough.
    if (isAuthenticated) auth0Logout({ returnTo: redirectURL })
  }

  const adminURL = businessID
    ? `${process.env.REACT_APP_ADMIN}/business/beta/accounts/edit/${businessID}`
    : `${process.env.REACT_APP_ADMIN}/business/beta/accounts/list`

  return (
    <>
      <button
        type="button"
        onClick={e => setOpen(e.currentTarget)}
        title={expanded ? undefined : user?.firstName ?? 'Account'}
        className="flex h-10 w-full items-center rounded-lg text-mm-neutral-1-200 transition-colors hover:bg-mm-secondary-900"
      >
        <span className="flex w-10 flex-shrink-0 justify-center">
          <ProfilePicture initials={initials} image={user?.profilePicture ?? ''} />
        </span>
        <span
          className={`overflow-hidden whitespace-nowrap text-sm font-medium transition-all duration-200 ${
            expanded ? 'ml-1 max-w-full opacity-100' : 'max-w-0 opacity-0'
          }`}
        >
          {user?.firstName}
        </span>
      </button>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setOpen(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {user?.permissions.includes(Permissions.AccessAdminApp) && (
          <MenuItem component="a" href={adminURL}>
            <i className="fad fa-cogs mr-2 w-5 text-center" aria-hidden /> Admin Panel
          </MenuItem>
        )}
        <MenuItem onClick={logoutUser}>
          <i className="fad fa-sign-out mr-2 w-5 text-center" aria-hidden /> Logout
        </MenuItem>
      </Menu>
    </>
  )
}
